import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, FolderOpen, X, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export default function GiftingCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", image: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/gifting-categories`, { headers: getAuthHeaders(), withCredentials: true });
      setCategories(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", image: "", description: "" });
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, image: cat.image || "", description: cat.description || "" });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      if (editing) {
        await axios.put(`${API}/gifting-categories/${editing.id}`, form, { headers: getAuthHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/gifting-categories`, form, { headers: getAuthHeaders(), withCredentials: true });
      }
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/gifting-categories/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      fetchCategories();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="page-gifting-categories" className="min-h-screen bg-[#F4F3EF]">
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-[#5A5955] mb-1">Master Data</p>
          <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Corporate Gifting Categories</h1>
        </div>
        <button onClick={openAdd} data-testid="add-category-btn"
          className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200">
          <Plus size={16} /> Add Category
        </button>
      </div>

      <div className="px-5 sm:px-8 py-6">
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : categories.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <FolderOpen size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No categories yet. Add one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <div key={cat.id} data-testid={`category-card-${cat.id}`} className="bg-white border border-[#D9D7D0] overflow-hidden group hover:shadow-lg transition-shadow">
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-[#F4F3EF] flex items-center justify-center">
                    <Image size={32} className="text-[#D9D7D0]" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-base font-bold text-[#161616] mb-2">{cat.name}</h3>
                  <p className="text-xs text-[#5A5955] leading-relaxed line-clamp-2">{cat.description || "No description"}</p>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#D9D7D0]">
                    <button onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.id}`} className="p-2 hover:bg-[#F4F3EF] transition-colors" title="Edit">
                      <Pencil size={14} className="text-[#5A5955]" />
                    </button>
                    <button onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`} className="p-2 hover:bg-red-50 transition-colors" title="Delete">
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="category-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">{editing ? "Edit Category" : "Add New Category"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="cat-input-name"
                  className="field" placeholder="Category name" />
              </div>
              <div>
                <label className="field-label">Image URL</label>
                <input type="url" value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} data-testid="cat-input-image"
                  className="field" placeholder="https://..." />
                {form.image && (
                  <img src={form.image} alt="Preview" className="mt-3 w-full h-32 object-cover border border-[#D9D7D0]" />
                )}
              </div>
              <div>
                <label className="field-label">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="cat-input-description"
                  className="field resize-y" placeholder="Brief description..." />
              </div>

              {formError && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{formError}</p>}

              <button type="submit" disabled={submitting} data-testid="submit-category-btn"
                className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200 disabled:opacity-50">
                {submitting ? "Saving..." : editing ? "Update Category" : "Add Category"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
